import { useState } from 'react';

import { useDebounced } from '@/shared/utils/useDebounced';

import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';
import { PlusIcon, SearchIcon } from '@/shared/ui/icons';

import { ClientEmail } from './ClientEmail';
import { ClientForm } from './ClientForm';
import { useClientList, useCreateClient } from './useClients';
import { addressLines, clientSubtitle, type Client } from './types';

/**
 * Choose the customer a quote is for, and add one without leaving the screen.
 *
 * The quick add is not a convenience, it is the difference between quoting today and
 * quoting tomorrow: the ERP has no API (contexto §6.1), so a customer who is not in the
 * local cache is a customer somebody has to type, and making that a separate trip to
 * another screen is how a quote waits a day.
 *
 * The search asks the API rather than filtering in the browser: the list is the whole
 * customer master and the API already searches name, ERP code and NIF at once, the NIF
 * normalised, so `B-39.074.992` finds the client stored as `B39074992`.
 *
 * It paints no heading of its own: the card it lives in already says "Cliente". The
 * search box carries its name for a screen reader (`aria-label`) and not as a visible
 * label, for the same reason: «Cliente» over «Buscar cliente» was the same word twice.
 */
export function ClientPicker({
  selected,
  onSelect,
  disabled,
  loading = false,
  error = null,
}: {
  selected: Client | null;
  onSelect: (client: Client | null) => void;
  disabled?: boolean;
  /** The quote points at a client and its card has not arrived yet. */
  loading?: boolean;
  /** The card could not be read at all. */
  error?: unknown;
}) {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const create = useCreateClient();
  const results = useClientList({ search: useDebounced(search).trim(), limit: 10 });

  // Three states, three answers. Collapsing them into `selected ?? null` showed the
  // search box for a quote that does have a customer, next to a button offering to
  // approve it: somebody would have approved it with the block blank.
  if (!selected && loading) {
    return <Spinner label="Cargando la ficha del cliente" />;
  }
  if (!selected && error) {
    return (
      <ErrorMessage error={error}>
        Este presupuesto tiene un cliente asignado y no se ha podido leer su ficha
      </ErrorMessage>
    );
  }

  if (selected) {
    return (
      <div className="flex flex-col gap-2" role="region" aria-label="Cliente">
        <div className="bg-paper border-line flex items-start justify-between gap-3 rounded-lg border px-3.5 py-3">
          <div className="min-w-0">
            <p className="text-ink text-[15px] leading-5 font-semibold">{selected.name}</p>
            <p className="muted">{clientSubtitle(selected)}</p>
            {addressLines(selected).map((line) => (
              <p key={line} className="text-ink-2 text-[13.5px] leading-5">
                {line}
              </p>
            ))}
            {selected.payment_terms ? (
              <p className="text-ink-2 mt-1 text-[13.5px] leading-5">
                Pago: {selected.payment_terms}
              </p>
            ) : null}
            <ClientEmail client={selected} {...(disabled ? { disabled } : {})} />
          </div>
          {!disabled ? (
            <Button variant="secondary" size="sm" onClick={() => onSelect(null)}>
              Cambiar
            </Button>
          ) : null}
        </div>
        {/* Said out loud because it is the one thing about this screen that surprises
            people: the card is copied into the quote when it is approved, and editing
            the client afterwards does not rewrite a document that was already sent. */}
        <p className="text-ink-3 text-[12.5px] leading-snug">
          Los datos se copian al presupuesto al aprobarlo. A partir de ahí, cambiar la ficha del
          cliente no cambia el documento.
        </p>
      </div>
    );
  }

  function closeAdding(): void {
    setAdding(false);
    // Otherwise the error of a failed attempt greets the next one before anything is typed.
    create.reset();
  }

  return (
    <div className="flex flex-col gap-3">
      {/* A sheet over the editor and not the form in place: the card this picker lives in
          is the narrow column of the quote editor, and the twelve fields of a client laid
          out inside it overlapped each other (seen on a desktop at 1280). The sheet is the
          same one Clientes → «Nuevo cliente» opens, so the form looks the same in both. */}
      {adding ? (
        <Dialog
          title="Cliente nuevo"
          description="Solo la razón social es obligatoria. Al crearlo queda elegido para este presupuesto."
          size="lg"
          onClose={closeAdding}
        >
          <ClientForm
            saving={create.isPending}
            error={create.isError ? create.error : null}
            submitLabel="Crear y elegir"
            onCancel={closeAdding}
            onSubmit={(payload) =>
              create.mutate(payload, {
                onSuccess: (client) => {
                  setAdding(false);
                  onSelect(client);
                },
              })
            }
          />
        </Dialog>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <SearchIcon
            size={16}
            className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Buscar cliente"
            placeholder="Nombre, código o NIF"
            className="field w-full pl-9"
            value={search}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {!disabled ? (
          <Button variant="secondary" icon={<PlusIcon size={16} />} onClick={() => setAdding(true)}>
            Cliente nuevo
          </Button>
        ) : null}
      </div>

      {results.isPending ? <Spinner label="Buscando clientes" /> : null}
      {results.isError ? (
        <ErrorMessage error={results.error}>No se pudo buscar el cliente</ErrorMessage>
      ) : null}

      {results.data && results.data.items.length > 0 ? (
        <ul className="divide-line-2 border-line bg-card divide-y rounded-md border">
          {results.data.items.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onSelect(client)}
                className="hover:bg-paper-2 flex w-full flex-col items-start px-3 py-2 text-left"
              >
                <span className="text-ink text-[14.5px] leading-5 font-medium">{client.name}</span>
                <span className="text-ink-3 text-[12.5px] leading-4">{clientSubtitle(client)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {results.data && results.data.items.length === 0 ? (
        <p className="text-ink-3 text-[13.5px]">
          {search.trim()
            ? 'Ningún cliente con ese nombre, código ni NIF. Puedes darlo de alta aquí mismo.'
            : 'Escribe para buscar, o da de alta uno nuevo.'}
        </p>
      ) : null}
    </div>
  );
}
